import type { JSX } from 'react';

import { withI18nPlaceholders } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionGamma/petition-gamma.yml';

import { withPrefetchedData } from 'src/shared/prefetch';

import { PetitionHeaderContainer } from 'src/app/pages/petitionGamma/containers/Header';
import type { PetitionDetailsPagePrefetchedDataProps } from 'src/app/pages/petitionGamma/details/pageContext';
import {
	PetitionDetailsPageContextProvider,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/petitionGamma/details/pageContext';
import type { PetitionGammaCommonProps } from 'src/app/pages/petitionGamma/shared/types';
import { isLoaded } from 'src/app/shared/utils/async';

import { DetailsWithContext } from './DetailsWithContext';
import { PetitionDetailsErrorHandler } from './ErrorHandler';

/**
 * Renders all of the content of the Gamma petition page. This is basically the real "entry point" that a simpler page
 * would reference from a routes.config file. There are layers between the routes and this because:
 *  1. The old/gamma version switch is handled in the old page's folder. (See `PetitionDetailsVersionRoot`)
 *  2. The share routes wrap the page in extra context based on route params. (Gamma has its own parallel implementation
 *     of this.)
 */
export const CommonPetitionDetails = withI18nPlaceholders(
	I18N_PLACEHOLDERS,
	withPrefetchedData(
		function CommonPetitionDetails({
			prefetchedData,
			prefetchedUserData,
			userInfoState,
		}: PetitionGammaCommonProps & PetitionDetailsPagePrefetchedDataProps): JSX.Element {
			const { slug } = useKnownAvailableParams<{ slug: string }>();

			return (
				<>
					<PetitionHeaderContainer slug={slug} userInfoState={userInfoState} />
					<div data-qa="petition-gamma">
						<PetitionDetailsPageContextProvider
							slug={slug}
							prefetchedData={prefetchedData}
							prefetchedUserData={prefetchedUserData}
							userInfo={userInfoState}
							errorHandler={PetitionDetailsErrorHandler}
							loadingFallback={<Loader size="m" my={256} mx="auto" />}
						>
							<DetailsWithContext slug={slug} />
						</PetitionDetailsPageContextProvider>
					</div>
				</>
			);
		},
		{
			prefetchName: __MODULE_HASH__,
			prefetchDependencies: [PetitionHeaderContainer],
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
			prefetchData: async (context) => {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const data = await prefetchPageContext(context.params.slug!, context);
				return {
					...data,
					// FIXME this is a temporary way to provide the petitionId to the PX sensor for apps/front-end/src/app/createRoutes.tsx
					perimeterx:
						data.pageData && isLoaded(data.pageData)
							? {
									petitionId: data.pageData.pageData.petition.id,
								}
							: undefined,
				};
			},
		},
	),
);
